import { sanityClient } from './client';

// Types
import type { LegalPage, Article, ArticleCard, ArticleType, Category, LandingPage, AnnouncementBannerData } from './types';

/**
 * Fetches a legal page by its slug
 */
export const getLegalPageBySlug = async (slug: string): Promise<LegalPage | null> => {
  const query = `*[_type == "legalPage" && slug.current == $slug][0]{
    _id,
    title,
    "slug": slug.current,
    description,
    sections[]{
      _key,
      heading,
      content
    },
    seo
  }`;

  return sanityClient.fetch<LegalPage | null>(query, { slug });
};

/**
 * Fetches all legal pages (for listing/navigation)
 */
export const getAllLegalPages = async (): Promise<Pick<LegalPage, '_id' | 'title' | 'slug'>[]> => {
  const query = `*[_type == "legalPage"]{
    _id,
    title,
    "slug": slug.current
  }`;

  return sanityClient.fetch(query);
};

// Article card projection for listing pages
const articleCardProjection = `{
  _id,
  title,
  "slug": slug.current,
  excerpt,
  "author": author->{
    name
  },
  "categories": categories[]->{
    title,
    "slug": slug.current
  },
  articleType,
  isFeatured,
  "featuredImage": featuredImage{
    "url": asset->url,
    alt
  },
  publishedAt
}`;

// Full article projection for detail pages
const articleDetailProjection = `{
  _id,
  _createdAt,
  _updatedAt,
  title,
  "slug": slug.current,
  subtitle,
  excerpt,
  "author": author->{
    _id,
    name,
    "slug": slug.current,
    "image": image{
      "url": asset->url,
      alt
    },
    bio
  },
  "categories": categories[]->{
    _id,
    title,
    "slug": slug.current,
    description
  },
  articleType,
  tags,
  "featuredImage": featuredImage{
    "url": asset->url,
    alt
  },
  "bannerImage": select(
    defined(bannerImage.asset) => bannerImage{
      "url": asset->url,
      alt
    },
    featuredImage{
      "url": asset->url,
      alt
    }
  ),
  body,
  "hubspotForm": hubspotForm{
    formId,
    title,
    description,
    buttonText,
    successMessage,
    "image": image{
      "url": asset->url,
      alt
    }
  },
  publishedAt,
  "seo": seo{
    metaTitle,
    metaDescription,
    "ogImage": ogImage{
      "url": asset->url,
      alt
    }
  },
  "relatedArticles": relatedArticles[]->${articleCardProjection}
}`;

/**
 * Fetches all articles for the resources listing page
 */
export const getAllArticles = async (): Promise<ArticleCard[]> => {
  const query = `*[_type == "article"] | order(publishedAt desc) ${articleCardProjection}`;
  return sanityClient.fetch<ArticleCard[]>(query);
};

/**
 * Fetches articles filtered by type (article, playbook, news)
 */
export const getArticlesByType = async (type: ArticleType): Promise<ArticleCard[]> => {
  const query = `*[_type == "article" && articleType == $type] | order(publishedAt desc) ${articleCardProjection}`;
  return sanityClient.fetch<ArticleCard[]>(query, { type });
};

/**
 * Fetches articles filtered by category slug
 */
export const getArticlesByCategory = async (categorySlug: string): Promise<ArticleCard[]> => {
  const query = `*[_type == "article" && $categorySlug in categories[]->slug.current] | order(publishedAt desc) ${articleCardProjection}`;
  return sanityClient.fetch<ArticleCard[]>(query, { categorySlug });
};

/**
 * Fetches the featured article (the one marked as isFeatured)
 */
export const getFeaturedArticle = async (): Promise<ArticleCard | null> => {
  const query = `*[_type == "article" && isFeatured == true][0] ${articleCardProjection}`;
  return sanityClient.fetch<ArticleCard | null>(query);
};

/**
 * Fetches a single article by slug for the detail page
 */
export const getArticleBySlug = async (slug: string): Promise<Article | null> => {
  const query = `*[_type == "article" && slug.current == $slug][0] ${articleDetailProjection}`;
  return sanityClient.fetch<Article | null>(query, { slug });
};

/**
 * Search articles by text query
 */
export const searchArticles = async (searchTerm: string): Promise<ArticleCard[]> => {
  const query = `*[_type == "article" && (
    title match $searchTerm ||
    excerpt match $searchTerm ||
    $searchTerm in tags
  )] | order(publishedAt desc) ${articleCardProjection}`;
  return sanityClient.fetch<ArticleCard[]>(query, { searchTerm: `*${searchTerm}*` });
};

/**
 * Fetches all categories
 */
export const getAllCategories = async (): Promise<Category[]> => {
  const query = `*[_type == "category"] | order(title asc){
    _id,
    title,
    "slug": slug.current,
    description
  }`;
  return sanityClient.fetch<Category[]>(query);
};

/** Author list item for filter dropdowns */
export type AuthorListItem = {
  _id: string;
  name: string;
  slug: string;
};

/**
 * Fetches all authors (for filter dropdowns)
 */
export const getAllAuthors = async (): Promise<AuthorListItem[]> => {
  const query = `*[_type == "author"] | order(name asc){
    _id,
    name,
    "slug": slug.current
  }`;
  return sanityClient.fetch<AuthorListItem[]>(query);
};

/**
 * Fetches articles with flexible filters
 */
export type ArticleFilters = {
  type?: ArticleType;
  categorySlug?: string;
  authorSlug?: string;
  searchTerm?: string;
  limit?: number;
};

export const getFilteredArticles = async (filters: ArticleFilters = {}): Promise<ArticleCard[]> => {
  const { type, categorySlug, authorSlug, searchTerm, limit } = filters;
  
  // Build filter conditions
  const conditions = ['_type == "article"'];
  
  if (type) {
    conditions.push('articleType == $type');
  }
  
  if (categorySlug) {
    conditions.push('$categorySlug in categories[]->slug.current');
  }
  
  if (authorSlug) {
    conditions.push('author->slug.current == $authorSlug');
  }
  
  if (searchTerm) {
    conditions.push(`(
      title match $searchTerm ||
      excerpt match $searchTerm ||
      $searchTerm in tags ||
      author->name match $searchTerm
    )`);
  }
  
  const filterString = conditions.join(' && ');
  const limitString = limit ? `[0...${limit}]` : '';
  
  const query = `*[${filterString}] | order(publishedAt desc)${limitString} ${articleCardProjection}`;
  
  return sanityClient.fetch<ArticleCard[]>(query, {
    type,
    categorySlug,
    authorSlug,
    searchTerm: searchTerm ? `*${searchTerm}*` : undefined,
  });
};

// Landing Page projection
const landingPageProjection = `{
  _id,
  title,
  "slug": slug.current,
  audienceTag,
  heroTitle,
  heroDescription,
  ctaText,
  ctaLink,
  valuePropositions[]{
    _key,
    title,
    description,
    vectorType,
    textPosition
  },
  letsTalkTitle,
  letsTalkDescription,
  letsTalkCtaText,
  letsTalkCtaLink,
  seo,
  isActive
}`;

/**
 * Fetches a landing page by its slug
 */
export const getLandingPageBySlug = async (slug: string): Promise<LandingPage | null> => {
  const query = `*[_type == "landingPage" && slug.current == $slug && isActive != false][0] ${landingPageProjection}`;
  return sanityClient.fetch<LandingPage | null>(query, { slug });
};

/**
 * Fetches all landing page slugs (for validation, prefetch, or sitemap)
 */
export const getAllLandingSlugs = async (): Promise<{ slug: string }[]> => {
  const query = `*[_type == "landingPage" && isActive != false] | order(priority asc){
    "slug": slug.current
  }`;
  return sanityClient.fetch(query);
};

/**
 * Fetches the active announcement banner (singleton — returns the first active one).
 * Only returns a banner if the linked article is published or custom title + URL are set.
 */
export const getAnnouncementBanner = async (): Promise<AnnouncementBannerData | null> => {
  const query = `*[
    _type == "announcementBanner"
    && isActive == true
    && (
      (defined(linkedArticle) && defined(linkedArticle->slug.current))
      || (defined(customTitle) && defined(customUrl))
    )
  ][0]{
    _id,
    isActive,
    label,
    "title": select(
      defined(linkedArticle) && defined(linkedArticle->slug.current) => linkedArticle->title,
      customTitle
    ),
    "href": select(
      defined(linkedArticle) && defined(linkedArticle->slug.current) => "/resources/" + linkedArticle->slug.current,
      customUrl
    )
  }`;
  return sanityClient.fetch<AnnouncementBannerData | null>(query);
};
